/**
 * Outlook-grade rich-text editor based on TipTap.
 *
 * Toolbar:
 *   • Font family + size
 *   • Bold / Italic / Underline / Strike
 *   • Text color + Highlight color (popovers)
 *   • Headings H1/H2/H3
 *   • Lists (bulleted / numbered) + Quote
 *   • Align (left / center / right / justify)
 *   • Link / Image (URL prompt)
 *   • Insert Table + table-row / table-col controls (when caret in table)
 *   • Clear formatting / Undo / Redo
 *
 * Exposes `registerInsertHandler(fn)` so a parent can imperatively insert a
 * Mustache merge field at the caret.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { FontFamily } from '@tiptap/extension-font-family';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import { Mark } from '@tiptap/core';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import {
  Bold, Italic, Strikethrough, Underline as UnderlineIcon, List, ListOrdered,
  Heading1, Heading2, Heading3, Quote, Link as LinkIcon, Eraser, Undo, Redo,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Image as ImageIcon, Upload,
  Table as TableIcon, Plus, Minus, Palette, Highlighter
} from 'lucide-react';

// ----- Custom FontSize mark (TipTap doesn't ship one) -----
const FontSize = Mark.create({
  name: 'fontSize',
  addOptions: () => ({ types: ['textStyle'] }),
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: el => el.style.fontSize?.replace(/['"]+/g, '') || null,
          renderHTML: attrs => attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {},
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: (size) => ({ chain }) => chain().setMark('textStyle', { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }) => chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

const FONT_FAMILIES = [
  { label: 'Calibri', value: 'Calibri, Arial, sans-serif' },
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Courier New', value: '"Courier New", Courier, monospace' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
];
const FONT_SIZES = ['10px', '11px', '12px', '13px', '14px', '15px', '16px', '18px', '20px', '24px', '28px', '32px'];

const COLORS = [
  '#000000', '#1f3864', '#1d4ed8', '#0ea5e9', '#10b981',
  '#22c55e', '#eab308', '#f97316', '#ef4444', '#a855f7',
  '#ec4899', '#6b7280', '#9ca3af', '#374151', '#ffffff',
];
const HIGHLIGHTS = [
  '#fff3a3', '#fef08a', '#bbf7d0', '#bae6fd', '#ddd6fe',
  '#fbcfe8', '#fed7aa', '#e5e7eb', null, // null = remove
];

const ToolbarBtn = ({ onClick, active, disabled, label, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
    data-testid={testid}
    className={`p-1.5 rounded transition-colors ${
      active ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
    } disabled:opacity-30 disabled:cursor-not-allowed`}
  >
    {children}
  </button>
);

const Popover = ({ open, onClose, children }) => (
  open ? (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div className="absolute z-40 mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-2">
        {children}
      </div>
    </>
  ) : null
);

const RichTextEditor = ({
  value,
  onChange,
  placeholder = 'Type your email body here...',
  registerInsertHandler,
  minHeight = 260,
  testidPrefix = 'tpl',
}) => {
  const [colorOpen, setColorOpen] = useState(false);
  const [highlightOpen, setHighlightOpen] = useState(false);
  const [tableOpen, setTableOpen] = useState(false);
  const colorBtnRef = useRef(null);
  const highlightBtnRef = useRef(null);
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: false, underline: false }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: 'text-blue-600 underline' } }),
      Placeholder.configure({ placeholder }),
      TextStyle,
      Color.configure({ types: ['textStyle'] }),
      Highlight.configure({ multicolor: true }),
      FontFamily.configure({ types: ['textStyle'] }),
      FontSize,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Image.configure({ inline: false, allowBase64: true, HTMLAttributes: { class: 'max-w-full h-auto rounded' } }),
      Table.configure({ resizable: true, HTMLAttributes: { class: 'tiptap-table' } }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none p-4 focus:outline-none email-editor',
        style: `min-height: ${minHeight}px`,
        'data-testid': `${testidPrefix}-body-editor`,
      },
    },
  });

  // Sync external value updates without disrupting cursor
  useEffect(() => {
    if (!editor) return;
    if ((value || '') !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
    // eslint-disable-next-line
  }, [editor, value]);

  // Expose merge-field insert
  useEffect(() => {
    if (!editor || !registerInsertHandler) return;
    registerInsertHandler((token) => editor.chain().focus().insertContent(token).run());
    // eslint-disable-next-line
  }, [editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('Link URL', prev || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const insertImage = () => {
    const url = window.prompt('Image URL (https://...)');
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  // Upload an image file from the user's device and embed it inline as a
  // base64 data URL. This is the standard approach for email signatures /
  // inline logos — Gmail, Outlook and Apple Mail all render base64 images
  // natively, and we don't need a public hosting URL for the embed to work.
  const MAX_UPLOAD_BYTES = 2 * 1024 * 1024; // 2 MB hard cap (after which the email gets too heavy)
  const onPickImage = () => fileInputRef.current?.click();
  const onImageFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      window.alert('Please pick an image file (PNG, JPG, GIF, SVG, WebP).');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      window.alert(
        `Image is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). ` +
        `Please use an image under 2 MB so the email stays under common SMTP limits.`
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== 'string') return;
      editor.chain().focus().setImage({ src: dataUrl, alt: file.name }).run();
    };
    reader.onerror = () => window.alert('Failed to read the image file. Please try a different one.');
    reader.readAsDataURL(file);
  };

  const isInTable = editor.isActive('table');

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      <style>{`
        .email-editor table.tiptap-table { border-collapse: collapse; margin: 0.5rem 0; table-layout: fixed; width: 100%; }
        .email-editor table.tiptap-table td, .email-editor table.tiptap-table th { border: 1px solid #cbd5e1; padding: 6px 8px; min-width: 1em; vertical-align: top; }
        .email-editor table.tiptap-table th { background: #f1f5f9; font-weight: 600; }
        .email-editor table.tiptap-table .selectedCell:after { background: rgba(59,130,246,0.12); content: ''; left: 0; right: 0; top: 0; bottom: 0; pointer-events: none; position: absolute; z-index: 2; }
        .email-editor img { display: block; max-width: 100%; height: auto; }
        /* List + heading + quote styling — Tailwind Preflight strips these
           by default, so we restore them locally so the toolbar buttons
           produce visible bullets/numbers. Without this the clicks look
           like no-ops even though the underlying HTML is correct. */
        .email-editor ul { list-style: disc outside; padding-left: 1.5rem; margin: 0.5rem 0; }
        .email-editor ol { list-style: decimal outside; padding-left: 1.5rem; margin: 0.5rem 0; }
        .email-editor ul ul { list-style: circle outside; }
        .email-editor ul ul ul { list-style: square outside; }
        .email-editor li { margin: 0.15rem 0; }
        .email-editor li > p { margin: 0; }
        .email-editor h1 { font-size: 1.6em; font-weight: 700; margin: 0.6em 0 0.4em; line-height: 1.25; }
        .email-editor h2 { font-size: 1.35em; font-weight: 700; margin: 0.5em 0 0.35em; line-height: 1.3; }
        .email-editor h3 { font-size: 1.15em; font-weight: 600; margin: 0.4em 0 0.3em; line-height: 1.35; }
        .email-editor blockquote { border-left: 3px solid #cbd5e1; padding: 0.25rem 0.75rem; margin: 0.5rem 0; color: #475569; font-style: italic; background: #f8fafc; }
        .email-editor p { margin: 0.4em 0; }
        .email-editor a { color: #2563eb; text-decoration: underline; }
        .email-editor hr { border: 0; border-top: 1px solid #e5e7eb; margin: 0.75em 0; }
        .email-editor .is-editor-empty:first-child::before { content: attr(data-placeholder); float: left; color: #9ca3af; pointer-events: none; height: 0; }
      `}</style>
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200">
        {/* Font family */}
        <select
          onChange={e => editor.chain().focus().setFontFamily(e.target.value).run()}
          value={editor.getAttributes('textStyle').fontFamily || ''}
          className="px-2 py-1 text-xs border border-gray-200 rounded bg-white"
          title="Font family"
          data-testid={`${testidPrefix}-font-family`}
        >
          <option value="">Default font</option>
          {FONT_FAMILIES.map(f => <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>)}
        </select>
        {/* Font size */}
        <select
          onChange={e => e.target.value ? editor.chain().focus().setFontSize(e.target.value).run() : editor.chain().focus().unsetFontSize().run()}
          value={editor.getAttributes('textStyle').fontSize || ''}
          className="px-2 py-1 text-xs border border-gray-200 rounded bg-white"
          title="Font size"
          data-testid={`${testidPrefix}-font-size`}
        >
          <option value="">Size</option>
          {FONT_SIZES.map(s => <option key={s} value={s}>{s.replace('px', '')}</option>)}
        </select>
        <span className="mx-1 h-5 w-px bg-gray-300" />

        <ToolbarBtn label="Bold" testid={`${testidPrefix}-fmt-bold`}
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}><Bold size={15} /></ToolbarBtn>
        <ToolbarBtn label="Italic" testid={`${testidPrefix}-fmt-italic`}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}><Italic size={15} /></ToolbarBtn>
        <ToolbarBtn label="Underline" testid={`${testidPrefix}-fmt-underline`}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}><UnderlineIcon size={15} /></ToolbarBtn>
        <ToolbarBtn label="Strikethrough"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}><Strikethrough size={15} /></ToolbarBtn>

        {/* Text color */}
        <div className="relative">
          <ToolbarBtn label="Text color" testid={`${testidPrefix}-fmt-color`}
            onClick={() => { setColorOpen(!colorOpen); setHighlightOpen(false); }}
            active={!!editor.getAttributes('textStyle').color}>
            <span ref={colorBtnRef} className="flex flex-col items-center">
              <Palette size={15} />
              <span className="block w-4 h-1 rounded-sm" style={{ background: editor.getAttributes('textStyle').color || '#1f3864' }} />
            </span>
          </ToolbarBtn>
          <Popover open={colorOpen} onClose={() => setColorOpen(false)}>
            <div className="grid grid-cols-5 gap-1 w-44">
              {COLORS.map(c => (
                <button key={c} type="button" onClick={() => { editor.chain().focus().setColor(c).run(); setColorOpen(false); }} className="h-6 w-6 rounded border border-gray-200 hover:scale-110 transition-transform" style={{ background: c }} aria-label={c} />
              ))}
              <button type="button" onClick={() => { editor.chain().focus().unsetColor().run(); setColorOpen(false); }} className="col-span-5 mt-1 text-xs text-blue-600 hover:underline">Reset</button>
            </div>
          </Popover>
        </div>

        {/* Highlight */}
        <div className="relative">
          <ToolbarBtn label="Highlight" testid={`${testidPrefix}-fmt-highlight`}
            onClick={() => { setHighlightOpen(!highlightOpen); setColorOpen(false); }}
            active={editor.isActive('highlight')}>
            <span ref={highlightBtnRef}><Highlighter size={15} /></span>
          </ToolbarBtn>
          <Popover open={highlightOpen} onClose={() => setHighlightOpen(false)}>
            <div className="grid grid-cols-4 gap-1 w-36">
              {HIGHLIGHTS.map((c, i) => c ? (
                <button key={c} type="button" onClick={() => { editor.chain().focus().toggleHighlight({ color: c }).run(); setHighlightOpen(false); }} className="h-6 w-6 rounded border border-gray-200 hover:scale-110" style={{ background: c }} />
              ) : (
                <button key={`reset-${i}`} type="button" onClick={() => { editor.chain().focus().unsetHighlight().run(); setHighlightOpen(false); }} className="col-span-3 text-xs text-blue-600 hover:underline">Reset</button>
              ))}
            </div>
          </Popover>
        </div>

        <span className="mx-1 h-5 w-px bg-gray-300" />

        <ToolbarBtn label="H1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })}><Heading1 size={15} /></ToolbarBtn>
        <ToolbarBtn label="H2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })}><Heading2 size={15} /></ToolbarBtn>
        <ToolbarBtn label="H3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })}><Heading3 size={15} /></ToolbarBtn>
        <ToolbarBtn label="Bulleted list" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')}><List size={15} /></ToolbarBtn>
        <ToolbarBtn label="Numbered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')}><ListOrdered size={15} /></ToolbarBtn>
        <ToolbarBtn label="Quote" onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')}><Quote size={15} /></ToolbarBtn>

        <span className="mx-1 h-5 w-px bg-gray-300" />

        <ToolbarBtn label="Align left" onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })}><AlignLeft size={15} /></ToolbarBtn>
        <ToolbarBtn label="Align center" onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })}><AlignCenter size={15} /></ToolbarBtn>
        <ToolbarBtn label="Align right" onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })}><AlignRight size={15} /></ToolbarBtn>
        <ToolbarBtn label="Justify" onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })}><AlignJustify size={15} /></ToolbarBtn>

        <span className="mx-1 h-5 w-px bg-gray-300" />

        <ToolbarBtn label="Link" onClick={setLink} active={editor.isActive('link')}><LinkIcon size={15} /></ToolbarBtn>
        <ToolbarBtn label="Upload image (PNG/JPG/SVG, ≤2 MB)" onClick={onPickImage} testid={`${testidPrefix}-fmt-image-upload`}><Upload size={15} /></ToolbarBtn>
        <ToolbarBtn label="Image from URL" onClick={insertImage} testid={`${testidPrefix}-fmt-image`}><ImageIcon size={15} /></ToolbarBtn>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onImageFileChange}
          style={{ display: 'none' }}
          data-testid={`${testidPrefix}-image-file-input`}
        />

        {/* Table menu */}
        <div className="relative">
          <ToolbarBtn label="Insert / edit table" onClick={() => setTableOpen(!tableOpen)} active={isInTable} testid={`${testidPrefix}-fmt-table`}>
            <TableIcon size={15} />
          </ToolbarBtn>
          <Popover open={tableOpen} onClose={() => setTableOpen(false)}>
            <div className="w-56 text-xs space-y-1">
              <button type="button" className="w-full text-left px-2 py-1 hover:bg-blue-50 rounded flex items-center gap-2" onClick={() => { editor.chain().focus().insertTable({ rows: 3, cols: 4, withHeaderRow: true }).run(); setTableOpen(false); }}>
                <Plus size={12} /> Insert 3×4 with header
              </button>
              <button type="button" className="w-full text-left px-2 py-1 hover:bg-blue-50 rounded flex items-center gap-2" onClick={() => { editor.chain().focus().insertTable({ rows: 5, cols: 6, withHeaderRow: true }).run(); setTableOpen(false); }}>
                <Plus size={12} /> Insert 5×6 with header
              </button>
              {isInTable && (
                <>
                  <hr className="my-1" />
                  <button type="button" className="w-full text-left px-2 py-1 hover:bg-blue-50 rounded flex items-center gap-2" onClick={() => editor.chain().focus().addRowAfter().run()}><Plus size={12} /> Add row</button>
                  <button type="button" className="w-full text-left px-2 py-1 hover:bg-red-50 rounded text-red-700 flex items-center gap-2" onClick={() => editor.chain().focus().deleteRow().run()}><Minus size={12} /> Delete row</button>
                  <button type="button" className="w-full text-left px-2 py-1 hover:bg-blue-50 rounded flex items-center gap-2" onClick={() => editor.chain().focus().addColumnAfter().run()}><Plus size={12} /> Add column</button>
                  <button type="button" className="w-full text-left px-2 py-1 hover:bg-red-50 rounded text-red-700 flex items-center gap-2" onClick={() => editor.chain().focus().deleteColumn().run()}><Minus size={12} /> Delete column</button>
                  <button type="button" className="w-full text-left px-2 py-1 hover:bg-red-50 rounded text-red-700 flex items-center gap-2" onClick={() => editor.chain().focus().deleteTable().run()}><Minus size={12} /> Delete table</button>
                </>
              )}
            </div>
          </Popover>
        </div>

        <span className="mx-1 h-5 w-px bg-gray-300" />

        <ToolbarBtn label="Clear formatting" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}><Eraser size={15} /></ToolbarBtn>
        <ToolbarBtn label="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}><Undo size={15} /></ToolbarBtn>
        <ToolbarBtn label="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}><Redo size={15} /></ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
